import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, Check, Info, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { pillars, getPillarBySlug } from '../data/pillars';
import { PillarLeadCapture } from '../components/PillarLeadCapture';
import { SiteNav } from '../components/SiteNav';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export default function PillarPage() {
  const { slug } = useParams();
  const pillar = getPillarBySlug(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!pillar) return <Navigate to="/" replace />;

  const Icon = pillar.icon;
  const others = pillars.filter((p) => p.slug !== slug);

  return (
    <div className="min-h-screen bg-[#FAF8F7] text-[#2B2625]" data-testid={`pillar-page-${pillar.slug}`}>
      <SiteNav />

      {/* Hero */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute -top-24 right-0 w-[460px] h-[460px] bg-[#E8D5D1]/50 rounded-full blur-[130px]" />
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 border border-[#E8D5D1] rounded-full text-[#C28B87] text-sm font-semibold mb-6">
                <Icon className="w-4 h-4" strokeWidth={1.8} /> {pillar.title}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2B2625] mb-5 font-medium leading-[1.05]">{pillar.headline}</h1>
              <p className="text-lg lg:text-xl text-[#6C6361] leading-relaxed mb-8 max-w-xl">{pillar.tagline}</p>
              <Link to="/?waitlist=true">
                <Button className="bg-[#C28B87] text-white hover:bg-[#b07b77] rounded-full px-8 py-6 text-base lg:text-lg font-medium shadow-[0_10px_30px_rgba(194,139,135,0.35)] transition-transform hover:scale-[1.02]" data-testid="pillar-hero-waitlist-btn">
                  Join the Waitlist <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
            <motion.div className="relative" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
              <div className="aspect-[4/5] max-w-md mx-auto lg:ml-auto rounded-[2.5rem] overflow-hidden shadow-[0_40px_80px_-24px_rgba(43,38,37,0.22)]">
                <img src={pillar.image} alt={pillar.title} className="w-full h-full object-cover" loading="eager" />
              </div>
              <div className="absolute -bottom-5 -left-5 w-24 h-24 bg-[#E8D5D1] rounded-full -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-[#C28B87] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4">{pillar.eyebrow}</p>
            <div className="space-y-6">
              {pillar.overview.map((para, idx) => (
                <p key={idx} className="text-lg text-[#6C6361] leading-relaxed">{para}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Rejuven helps */}
      <section className="py-20 lg:py-24 bg-gradient-to-b from-[#FAF8F7] via-[#F3EFEF]/50 to-[#FAF8F7]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div className="mb-12 max-w-2xl" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-[#C28B87] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4">How Rejuven Helps</p>
            <h2 className="font-display text-3xl sm:text-4xl text-[#2B2625] font-medium">Personalized, preventative, practical</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {pillar.helps.map((h, idx) => (
              <motion.div key={idx} className="bg-white rounded-3xl p-8 border border-[#E8D5D1]/70 shadow-[0_8px_32px_rgba(43,38,37,0.04)]" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }}>
                <div className="w-12 h-12 rounded-2xl bg-[#F3EFEF] flex items-center justify-center mb-5"><Check className="w-6 h-6 text-[#C28B87]" strokeWidth={1.8} /></div>
                <h3 className="font-display text-xl text-[#2B2625] mb-3 font-medium">{h.title}</h3>
                <p className="text-[#6C6361] text-sm leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Focus areas */}
          <motion.div className="mt-14" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h3 className="font-display text-2xl text-[#2B2625] mb-5 font-medium">What we focus on</h3>
            <div className="flex flex-wrap gap-3">
              {pillar.focusAreas.map((area) => (
                <span key={area} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E8D5D1] rounded-full text-sm text-[#6C6361]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C28B87]" /> {area}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Educational note (peptides / hormones) */}
          {pillar.note && (
            <div className="mt-12 bg-[#F3EFEF] rounded-2xl p-6 flex items-start gap-4">
              <Info className="w-5 h-5 text-[#899285] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#6C6361] leading-relaxed">{pillar.note}</p>
            </div>
          )}
        </div>
      </section>

      {/* Lead capture */}
      <PillarLeadCapture pillar={pillar.title} slug={pillar.slug} />

      {/* Explore other pillars */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.h2 className="font-display text-3xl sm:text-4xl text-[#2B2625] mb-10 font-medium" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>Explore more of what we support</motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {others.map((p) => {
              const PIcon = p.icon;
              return (
                <Link key={p.slug} to={`/pillars/${p.slug}`} className="group bg-[#FAF8F7] rounded-3xl p-6 border border-[#E8D5D1]/70 hover:-translate-y-1 hover:shadow-[0_20px_44px_-18px_rgba(43,38,37,0.16)] transition-[transform,box-shadow] duration-300" data-testid={`related-pillar-${p.slug}`}>
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center mb-4 group-hover:bg-[#E8D5D1] transition-colors"><PIcon className="w-6 h-6 text-[#C28B87]" strokeWidth={1.6} /></div>
                  <h3 className="font-display text-xl text-[#2B2625] mb-1 font-medium">{p.title}</h3>
                  <span className="inline-flex items-center gap-1 text-sm text-[#C28B87] group-hover:gap-2 transition-all">Explore <ChevronRight className="w-3 h-3" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 px-6 lg:px-12 bg-[#2B2625] text-white relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 left-0 w-[520px] h-[520px] bg-[#C28B87]/25 rounded-full blur-[150px]" /></div>
        <motion.div className="max-w-3xl mx-auto text-center relative z-10" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="text-[#E8D5D1] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-6">Private Beta</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-6 font-medium leading-[1.08]">Ready to explore {pillar.title.toLowerCase()} for your biology?</h2>
          <p className="text-lg text-white/65 mb-10 max-w-2xl mx-auto leading-relaxed">Join our private beta waitlist and get early access to personalized, preventative guidance built for you.</p>
          <Link to="/?waitlist=true"><Button className="bg-white text-[#2B2625] hover:bg-[#F3EFEF] rounded-full px-12 py-7 text-lg font-semibold transition-transform hover:scale-[1.02]">Join the Waitlist <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-[#221D1C] text-white py-14 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-8">
            <div className="bg-white/10 rounded-2xl px-6 py-3"><img src={LOGO_URL} alt="Rejuven" className="h-8" /></div>
            <div className="flex items-center gap-8 text-sm text-white/55">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <p className="text-white/35 text-sm">© 2026 Rejuven Inc.</p>
          </div>
          <div className="border-t border-white/10 pt-6">
            <p className="text-white/45 text-xs leading-relaxed max-w-4xl mx-auto text-center"><strong className="text-white/60">Medical disclaimer:</strong> Rejuven provides educational and preventative wellness information only. It is not medical advice and is not a substitute for professional diagnosis, treatment or the advice of your licensed physician. We do not diagnose, treat, cure or prevent any disease, and do not prescribe medications, hormones or peptides. Always consult a qualified healthcare provider before making changes to your health regimen.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
